import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { FaPlus, FaEdit, FaSpinner, FaEye, FaTimes, FaDownload } from 'react-icons/fa';
import { QRCodeSVG } from 'qrcode.react';

// QR Styling for the small list view
const qrCodeOptionsDashboard = {
    size: 64,
    bgColor: "#FFFFFF",
    fgColor: "#000000",
    level: "Q",
    includeMargin: false,
};

// QR Styling for the large modal view
const qrCodeOptionsModal = {
    size: 256,
    bgColor: "#FFFFFF",
    fgColor: "#000000",
    level: "Q",
    includeMargin: true, // Add some margin for better aesthetics
};

const DashboardPage = () => {
    const { currentUser, userData } = useAuth();
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // State to manage the QR code modal
    const [selectedProfileForModal, setSelectedProfileForModal] = useState(null);
    const qrCodeRef = useRef(null);

    // Fetching profiles logic (no changes here)
    useEffect(() => {
        const fetchProfiles = async () => {
            if (!currentUser?.uid) { setError("Kullanıcı kimliği doğrulanamadı."); setLoading(false); return; }
            setLoading(true); setError(''); setProfiles([]);
            try {
                const profilesRef = collection(db, 'vehicleProfiles');
                const q = query(profilesRef, where('userId', '==', currentUser.uid), orderBy('createdAt', 'desc'));
                const querySnapshot = await getDocs(q);
                const userProfiles = [];
                querySnapshot.forEach((doc) => { userProfiles.push({ id: doc.id, ...doc.data() }); });
                setProfiles(userProfiles);
            } catch (err) {
                console.error("Dashboard Fetch Error: ", err);
                setError(`Numaratörler yüklenirken hata oluştu (${err.code || 'Bilinmeyen'}).`);
            } finally { setLoading(false); }
        };
        if (currentUser) { fetchProfiles(); } else { setLoading(false); setError("Giriş yapmanız gerekiyor."); }
    }, [currentUser]);

    // Format date function (no changes here)
    const formatDate = (timestamp) => {
        if (!timestamp?.toDate) return 'Bilinmiyor';
        try { return timestamp.toDate().toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' }); }
        catch (e) { return 'Geçersiz Tarih'; }
    }

    // Function to handle downloading the QR code from the modal
    const handleDownload = () => {
        if (qrCodeRef.current) {
            const svgXML = new XMLSerializer().serializeToString(qrCodeRef.current);
            const dataUrl = `data:image/svg+xml;base64,${btoa(svgXML)}`;
            const img = new Image();

            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                const pngFile = canvas.toDataURL('image/png');

                const downloadLink = document.createElement('a');
                downloadLink.download = `${selectedProfileForModal.vehiclePlate || selectedProfileForModal.id}-QRCode.png`;
                downloadLink.href = pngFile;
                downloadLink.click();
            };
            img.src = dataUrl;
        }
    };


    return (
        <div className="container mx-auto px-4 py-10 max-w-3xl">
            {/* --- QR Code Modal --- */}
            {selectedProfileForModal && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4"
                    onClick={() => setSelectedProfileForModal(null)} // Close on background click
                >
                    <div
                        className="bg-white p-6 rounded-lg shadow-xl text-center relative max-w-sm w-full"
                        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
                    >
                        <button onClick={() => setSelectedProfileForModal(null)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-800" aria-label="Kapat">
                            <FaTimes size={24} />
                        </button>
                        <h3 className="text-xl font-bold mb-4 text-gray-800 truncate" title={selectedProfileForModal.vehiclePlate || `Kod: ${selectedProfileForModal.id}`}>
                            {selectedProfileForModal.vehiclePlate || `Kod: ${selectedProfileForModal.id}`}
                        </h3>
                        <div className="p-4 border inline-block">
                             <QRCodeSVG
                                ref={qrCodeRef} // Attach ref for downloading
                                value={`https://kodutara.web.app/q/${selectedProfileForModal.id}`}
                                {...qrCodeOptionsModal}
                            />
                        </div>
                        <p className="text-sm text-gray-500 mt-4">Bu kodu telefonunuzdan taratabilir veya indirebilirsiniz.</p>
                        <button onClick={handleDownload} className="mt-4 inline-flex items-center px-4 py-2 bg-black text-white rounded hover:bg-gray-800 transition-colors font-semibold">
                            <FaDownload className="mr-2" /> İndir (PNG)
                        </button>
                    </div>
                </div>
            )}


            <div className="bg-white shadow-md rounded-lg p-6 md:p-8">
                {/* Welcome Header */}
                <div className="flex justify-between items-center mb-6 border-b pb-4">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800"> Hoş Geldiniz, {userData?.displayName || 'Kullanıcı'}! </h1>
                </div>
                <h2 className="text-xl font-semibold mb-5 text-gray-700">Numaratörleriniz</h2>

                {/* Loading State */}
                {loading && (<div className="text-center py-10 text-gray-500"><FaSpinner className="animate-spin inline-block mr-2" size={20} /> Yükleniyor...</div>)}

                {/* Error State */}
                {!loading && error && ( <div className="text-center py-10 px-4 border-2 border-dashed border-red-300 rounded-lg bg-red-50">...</div> )}

                {/* Content Display */}
                {!loading && !error && (
                    <>
                        {/* Case 1: No Profiles */}
                        {profiles.length === 0 && (
                             <div className="text-center py-10 px-4 border-2 border-dashed border-gray-300 rounded-lg">
                                 {/* ... No changes needed here ... */}
                                 <p className="text-gray-600 mb-4"> Henüz hesabınıza bağlı bir Numaratör bulunmuyor. </p>
                                 <Link to="/link-qr" className="inline-flex items-center px-6 py-3 bg-black text-white rounded hover:bg-gray-800 transition-colors font-semibold"> <FaPlus className="mr-2" /> Yeni Numaratör Bağla </Link>
                             </div>
                        )}

                        {/* Case 2: Profiles Found */}
                        {profiles.length > 0 && (
                            <div className="space-y-4">
                                {profiles.map((profile) => (
                                    <div key={profile.id} className="border border-gray-200 rounded-lg p-4 flex flex-col sm:flex-row justify-between items-center hover:shadow-md transition-shadow duration-200 bg-white">
                                        {/* QR Code (now clickable) */}
                                        <div
                                            className="mb-3 sm:mb-0 sm:mr-4 flex-shrink-0 p-1 bg-white border rounded-md cursor-pointer hover:opacity-75 transition-opacity"
                                            onClick={() => setSelectedProfileForModal(profile)}
                                            title="Büyütmek için tıkla"
                                        >
                                            <QRCodeSVG
                                                value={`https://kodutara.web.app/q/${profile.id}`}
                                                {...qrCodeOptionsDashboard}
                                            />
                                        </div>

                                        {/* Profile Details */}
                                        <div className='flex-grow text-center sm:text-left mb-3 sm:mb-0 mr-4 overflow-hidden'>
                                            <p className="font-semibold text-lg text-gray-800 truncate" title={profile.vehiclePlate || `Kod: ${profile.id}`}>{profile.vehiclePlate || `Kod: ${profile.id}`}</p>
                                            <p className="text-sm text-gray-600 mt-1 line-clamp-1" title={profile.visibleNote || ''}>Not: {profile.visibleNote || <span className='italic text-gray-400'>Eklenmemiş</span>}</p>
                                            {profile.internalData?.muayeneGecerlilikTarihi && (
                                                <p className="text-xs text-blue-600 font-medium mt-1 inline-flex items-center bg-blue-50 px-2 py-0.5 rounded">
                                                    Muayene: {profile.internalData.muayeneGecerlilikTarihi}
                                                </p>
                                            )}
                                            <p className="text-xs text-gray-400 mt-2">Oluşturulma: {formatDate(profile.createdAt)}</p>
                                        </div>

                                        {/* --- Action Buttons --- */}
                                        <div className="flex-shrink-0 flex items-center space-x-2 self-center">
                                            {/* Preview Button */}
                                            <a href={`/q/${profile.id}`} target="_blank" rel="noopener noreferrer" className="p-2 text-green-600 hover:text-green-800 hover:bg-green-100 rounded-full transition-colors" aria-label={`Numaratör ${profile.id} önizle`} title="Önizle">
                                                <FaEye size={20} />
                                            </a>
                                            {/* Edit Button */}
                                            <button onClick={() => navigate(`/edit-profile/${profile.id}`)} className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-full transition-colors" aria-label={`Numaratör ${profile.id} düzenle`} title="Düzenle">
                                                <FaEdit size={20} />
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                {/* Add New Button Section */}
                                <div className="mt-8 pt-6 border-t text-center">
                                    <Link to="/link-qr" className="inline-flex items-center justify-center px-6 py-3 bg-black text-white rounded hover:bg-gray-800 transition-colors font-semibold">
                                        <FaPlus className="mr-2" /> Yeni Numaratör Bağla
                                    </Link>
                                    <p className="text-sm text-gray-500 mt-6">Yeni bir Numaratöre mi ihtiyacınız var? <Link to="/purchase" className="text-blue-600 hover:underline font-medium">Satın Alın</Link></p>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DashboardPage;